'use client';

import React, { useState, useCallback, ChangeEvent } from 'react';
import { Button } from '@/app/_module/components/ui/button';
import { Input } from '@/app/_module/components/ui/input';
import { Badge } from '@/app/_module/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/app/_module/components/ui/tooltip';
import {
  Mic,
  MicOff,
  PhoneOff,
  Pause,
  Play,
  UserPlus,
  Users,
  Loader2,
  User,
  X,
  Delete,
} from 'lucide-react';
import { ConferenceParticipant, ParticipantRole } from '../services/callService';
import { cn } from '@/app/_module/lib/utils';
import { useLeadByPhone } from '@/app/(authenticated)/leads/_modules/hooks/queries';

interface InlineAddPartyPanelProps {
  participants: ConferenceParticipant[];
  onMuteToggle: (participantSid: string, currentlyMuted: boolean) => void;
  onHoldToggle: (participantSid: string, currentlyOnHold: boolean) => void;
  onRemoveParticipant: (participantSid: string) => void;
  onAddParticipant: (phoneNumber: string, name?: string, role?: 'agent' | 'customer' | 'consultant') => Promise<boolean>;
  isAddingParticipant?: boolean;
  loadingParticipantSid?: string;
  className?: string;
  onClose?: () => void;
}

const roleColors: Record<ParticipantRole, string> = {
  agent: 'bg-blue-100 text-blue-700',
  customer: 'bg-green-100 text-green-700',
  consultant: 'bg-purple-100 text-purple-700',
};

const statusColors: Record<string, string> = {
  ringing: 'bg-yellow-100 text-yellow-700',
  connected: 'bg-green-100 text-green-700',
  hold: 'bg-orange-100 text-orange-700',
  disconnected: 'bg-gray-100 text-gray-500',
};

// Format phone number for display
const formatDialDisplay = (digits: string): string => {
  if (!digits) return "";
  const cleaned = digits.replace(/\D/g, "");
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

// Format phone number for participant display
const formatPhone = (phone: string): string => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

export function InlineAddPartyPanel({
  participants,
  onMuteToggle,
  onHoldToggle,
  onRemoveParticipant,
  onAddParticipant,
  isAddingParticipant = false,
  loadingParticipantSid,
  className,
  onClose,
}: InlineAddPartyPanelProps) {
  const [addPartyNumber, setAddPartyNumber] = useState("");
  const cleanedNumber = addPartyNumber.replace(/\D/g, "");

  // Look up lead by phone number
  const { data: matchedLead } = useLeadByPhone(
    cleanedNumber.length === 10 ? cleanedNumber : ""
  );

  const connectedCount = participants.filter(p => p.status === 'connected').length;

  // Handle dial pad press
  const handleDialPadPress = useCallback((digit: string) => {
    if (cleanedNumber.length < 10) {
      setAddPartyNumber(prev => prev + digit);
    }
  }, [cleanedNumber.length]);

  // Handle input change
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 10);
    setAddPartyNumber(digits);
  }, []);

  // Handle adding participant to call
  const handleAddToCall = useCallback(async () => {
    if (cleanedNumber.length !== 10) return;

    const formattedNumber = "+1" + cleanedNumber;
    const name = matchedLead?.name || undefined;

    const success = await onAddParticipant(formattedNumber, name);
    if (success) {
      setAddPartyNumber("");
    }
  }, [cleanedNumber, onAddParticipant, matchedLead]);

  return (
    <div className={cn('bg-blue-50 rounded-lg border border-blue-200', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-blue-200">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">Add Party to Call</span>
          {participants.length > 0 && (
            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
              {connectedCount} connected
            </Badge>
          )}
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="p-3">
        {/* Phone Input */}
        <div className="flex items-center gap-2 mb-2">
          <Input
            type="tel"
            value={formatDialDisplay(addPartyNumber)}
            onChange={handleInputChange}
            placeholder="(XXX) XXX-XXXX"
            className="text-center text-sm font-mono h-9 border-blue-200 focus:border-blue-500 flex-1 bg-white"
            maxLength={14}
          />
          {addPartyNumber && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full h-9 w-9 flex-shrink-0 border-blue-200"
              onClick={() => setAddPartyNumber(prev => prev.slice(0, -1))}
            >
              <Delete className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Lead Details - Show when full number entered and lead found */}
        {cleanedNumber.length === 10 && matchedLead ? (
          <div className="mb-2 p-2 rounded-lg border bg-white border-blue-200">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold">
                {matchedLead.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || 'L'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{matchedLead.name}</p>
                <p className="text-xs text-gray-500">{formatDialDisplay(addPartyNumber)}</p>
              </div>
            </div>
          </div>
        ) : cleanedNumber.length === 10 ? (
          <div className="mb-2 p-2 rounded-lg border bg-white border-gray-200">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0">
                <User className="h-3.5 w-3.5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Unknown Number</p>
                <p className="text-xs text-gray-500">{formatDialDisplay(addPartyNumber)}</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Dial Pad */}
        <div className="grid grid-cols-3 gap-1 mb-2">
          {[
            { digit: "1", letters: "" },
            { digit: "2", letters: "ABC" },
            { digit: "3", letters: "DEF" },
            { digit: "4", letters: "GHI" },
            { digit: "5", letters: "JKL" },
            { digit: "6", letters: "MNO" },
            { digit: "7", letters: "PQRS" },
            { digit: "8", letters: "TUV" },
            { digit: "9", letters: "WXYZ" },
            { digit: "*", letters: "" },
            { digit: "0", letters: "+" },
            { digit: "#", letters: "" },
          ].map(({ digit, letters }) => (
            <button
              key={digit}
              type="button"
              className="h-9 rounded-lg bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-200 transition-all flex flex-col items-center justify-center"
              onClick={() => handleDialPadPress(digit)}
            >
              <span className="text-base font-semibold text-gray-800">{digit}</span>
              {letters && (
                <span className="text-[7px] text-gray-400 tracking-wider">{letters}</span>
              )}
            </button>
          ))}
        </div>

        {/* Add to Call Button */}
        <Button
          className={`w-full h-10 text-sm font-medium rounded-lg ${
            cleanedNumber.length === 10
              ? "bg-green-500 hover:bg-green-600 text-white"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
          onClick={handleAddToCall}
          disabled={cleanedNumber.length !== 10 || isAddingParticipant}
        >
          {isAddingParticipant ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4 mr-2" />
              Add to Call
            </>
          )}
        </Button>

        {/* Current Participants */}
        {participants.length > 0 && (
          <div className="mt-3 pt-3 border-t border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-xs font-medium text-blue-800">
                {participants.length} participant{participants.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
              {participants.map((participant) => {
                const isLoadingThis = loadingParticipantSid === participant.participantSid;
                const isDisconnected = participant.status === 'disconnected';

                return (
                  <div
                    key={participant.participantSid}
                    className={cn(
                      'flex items-center justify-between p-2 bg-white rounded-lg border border-blue-100',
                      isDisconnected && 'opacity-50'
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className={`h-2 w-2 rounded-full flex-shrink-0 ${
                        participant.status === 'connected' ? 'bg-green-500' :
                        participant.status === 'ringing' ? 'bg-yellow-500 animate-pulse' :
                        participant.status === 'hold' ? 'bg-orange-500' : 'bg-gray-400'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <span className="text-xs font-medium truncate block">
                          {participant.name || formatPhone(participant.phoneNumber || '')}
                        </span>
                        <Badge className={cn('text-[9px] px-1 py-0', statusColors[participant.status])}>
                          {participant.status}
                        </Badge>
                      </div>
                    </div>
                    {!isDisconnected && (
                      <div className="flex items-center gap-0.5">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => onMuteToggle(participant.participantSid, participant.isMuted)}
                                disabled={isLoadingThis}
                              >
                                {isLoadingThis ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : participant.isMuted ? (
                                  <MicOff className="h-3 w-3 text-red-500" />
                                ) : (
                                  <Mic className="h-3 w-3" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {participant.isMuted ? 'Unmute' : 'Mute'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => onHoldToggle(participant.participantSid, participant.isOnHold)}
                                disabled={isLoadingThis}
                              >
                                {participant.isOnHold ? (
                                  <Play className="h-3 w-3 text-orange-500" />
                                ) : (
                                  <Pause className="h-3 w-3" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {participant.isOnHold ? 'Resume' : 'Hold'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6 text-red-500 hover:text-red-600"
                                onClick={() => onRemoveParticipant(participant.participantSid)}
                                disabled={isLoadingThis}
                              >
                                <PhoneOff className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove from call</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InlineAddPartyPanel;
